import React, { useEffect, useState, ReactElement } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import { AppBar, Toolbar, IconButton, Typography, useMediaQuery, useTheme, Box } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { useAuth } from './hooks/useAuth';
import { setupApiInterceptors } from './services/api';

// Import placeholder page components
import Dashboard from './pages/Dashboard';
import MyAis from './pages/MyAis';
import QnaData from './pages/QnaData';
import Review from './pages/Review';
import TestChatbot from './pages/TestChatbot';
import Demo from './pages/Demo';
import Analytics from './pages/Analytics';
import ChatWidget from './pages/ChatWidget';
import Login from './pages/Login';
import CreateVersion from './pages/CreateVersion';
import ShareAi from './pages/ShareAi';
import ImportAi from './pages/ImportAi';
import Tables from './pages/Tables'; // Import the Tables component
import ConversationDetail from './pages/ConversationDetail'; // Import the ConversationDetail component
import TestWidget from './pages/TestWidget'; // Import the TestWidget component for manual mode testing

// Import Sidebar component
import Sidebar from './components/Sidebar';

// Define types for the AuthenticatedRoute component
interface AuthenticatedRouteProps {
  element: ReactElement;
  path: string;
}

// AuthenticatedRoute component to handle auth checks with loading state
const AuthenticatedRoute: React.FC<AuthenticatedRouteProps> = ({ element, path }) => {
  const { user, token } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    // If token exists in localStorage but user state hasn't been set yet,
    // this gives a small delay to let auth initialize properly
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 500);
    
    return () => clearTimeout(timer);
  }, [user, token]);

  if (isLoading) {
    // During loading, don't redirect, just show nothing (or a loading spinner)
    return null;
  }
  
  // After loading is complete, check if user exists
  return user ? element : <Navigate to="/login" state={{ from: location }} replace />;
};

const App = () => {
  const { user, token } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    setupApiInterceptors(token);
  }, [token]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  return (
    <Router>
      <Box sx={{ display: 'flex' }}>
        {/* Mobile App Bar */}
        {user && isMobile && (
          <AppBar
            position="fixed"
            sx={{
              zIndex: theme.zIndex.drawer + 1,
              display: { xs: 'block', md: 'none' },
            }}
          >
            <Toolbar>
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ mr: 2 }}
              >
                <MenuIcon />
              </IconButton>
              <Typography variant="h6" noWrap component="div">
                Coachable AI
              </Typography>
            </Toolbar>
          </AppBar>
        )}

        {/* Sidebar */}
        {user && (
          <Sidebar 
            mobileOpen={mobileOpen}
            onMobileClose={handleDrawerToggle}
          />
        )}

        {/* Main content */}
        <Box
          component="main"
          sx={{
            flex: 1,
            p: { xs: 2, sm: 3 },
            mt: { xs: user ? 8 : 0, md: 0 }, // Add top margin on mobile when logged in (for app bar)
            minHeight: '100vh',
          }}
        >
          <Routes>
            <Route path="/" element={<AuthenticatedRoute element={<Dashboard />} path="/" />} />
            <Route path="/my-ais" element={<AuthenticatedRoute element={<MyAis />} path="/my-ais" />} />
            <Route path="/qna-data/:aiId" element={<AuthenticatedRoute element={<QnaData />} path="/qna-data/:aiId" />} />
            <Route path="/tables/:aiId" element={<AuthenticatedRoute element={<Tables />} path="/tables/:aiId" />} />
            {/* Version-specific review routes */}
            <Route path="/review/:aiId/versions/:collectionId" element={<AuthenticatedRoute element={<Review />} path="/review/:aiId/versions/:collectionId" />} />
            <Route path="/review/:aiId/versions/:collectionId/conversation/:conversationId" element={<AuthenticatedRoute element={<ConversationDetail />} path="/review/:aiId/versions/:collectionId/conversation/:conversationId" />} />
            {/* Legacy review routes for backward compatibility */}
            <Route path="/review/:aiId" element={<AuthenticatedRoute element={<Review />} path="/review/:aiId" />} />
            <Route path="/review/:aiId/conversation/:conversationId" element={<AuthenticatedRoute element={<ConversationDetail />} path="/review/:aiId/conversation/:conversationId" />} />
            <Route path="/test-chatbot/:aiId" element={<AuthenticatedRoute element={<TestChatbot />} path="/test-chatbot/:aiId" />} />
            <Route path="/test-widget" element={<AuthenticatedRoute element={<TestWidget />} path="/test-widget" />} />
            <Route path="/test-widget/:aiId" element={<AuthenticatedRoute element={<TestWidget />} path="/test-widget/:aiId" />} />
            <Route path="/analytics" element={<AuthenticatedRoute element={<Analytics />} path="/analytics" />} />
            <Route path="/create-version/:aiId" element={<AuthenticatedRoute element={<CreateVersion />} path="/create-version/:aiId" />} />
            <Route path="/share-ai/:aiId" element={<AuthenticatedRoute element={<ShareAi />} path="/share-ai/:aiId" />} />
            <Route path="/import-ai" element={<AuthenticatedRoute element={<ImportAi />} path="/import-ai" />} />
            <Route path="/chat/:aiId" element={<AuthenticatedRoute element={<ChatWidget />} path="/chat/:aiId" />} />
            {/* Public demo route - no authentication required */}
            <Route path="/demo/:aiId" element={<Demo />} />
            <Route path="/login" element={<Login />} />
          </Routes>
        </Box>
      </Box>
    </Router>
  );
};

export default App;
